import { datadogRum } from '@datadog/browser-rum';
import Cookies from 'js-cookie';
import React, {
  Dispatch,
  createContext,
  useContext,
  useEffect,
  useState
} from 'react';
import { useDispatch } from 'react-redux';
import { GET_CORE_CONFIG } from '|/ducks/core/actions';
import { Token, default as getAccessToken } from '|/helpers/access-token';
import googleTagManager from '|/helpers/analytics/google-tag-manager';
import AppFrameworkContext from '|/helpers/app-framework';
import { refreshTokenIfMissing } from '|/helpers/axios';

interface CoreConfig {
  hasProfile: boolean;
  userId?: string;
  email?: string;
  name?: string;
  isUserBeingImpersonated?: boolean;
}

const dispatchCoreConfig = (
  dispatch: Dispatch<any>,
  coreConfig: CoreConfig
) => {
  dispatch({
    type: `${GET_CORE_CONFIG}_SUCCESS`,
    payload: coreConfig
  });
};

const getCoreConfig = (accessToken: Token): CoreConfig => {
  const user = accessToken?.user;
  return {
    hasProfile: user?.name !== undefined,
    userId: user?.sub,
    email: user?.name,
    name: user?.name,
    isUserBeingImpersonated: user?.is_impersonating === 'True'
  };
};

const defaultContext = {
  accessToken: null as null | ReturnType<typeof getAccessToken>
};

const UserInitializationContext = createContext(defaultContext);

const UserInitializationProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children
}) => {
  const [{ isUserInitialized, accessToken }, setUser] = useState({
    isUserInitialized: false,
    ...defaultContext
  });
  const dispatch = useDispatch();
  const { load } = useContext(AppFrameworkContext);

  useEffect(() => {
    if (accessToken !== null) {
      if (accessToken.user?.name !== undefined)
        googleTagManager.trackUser('User Logged In', {
          userId: accessToken.user?.sub
        });
      datadogRum.setUser({
        id: accessToken?.user?.user_id,
        userBcid: Cookies.get('BrowserCookieId')
      });
      dispatchCoreConfig(dispatch, getCoreConfig(accessToken));
    }
  }, [dispatch, accessToken]);

  useEffect(() => {
    load(
      refreshTokenIfMissing().then(() => {
        setUser({ isUserInitialized: true, accessToken: getAccessToken() });
      })
    );
  }, [load]);

  if (!isUserInitialized) return null;

  return (
    <UserInitializationContext.Provider value={{ accessToken }}>
      {children}
    </UserInitializationContext.Provider>
  );
};

export default UserInitializationContext;
export { UserInitializationProvider };
